/**
 * Table-panel composition — the one place the individual panels are
 * assembled into the sidebar (extracted from main.ts so the composition
 * logic lives under coverage; META-06 caps exempt files at 200 lines and
 * main.ts is exempt precisely because it should contain no logic).
 *
 * What lives here and NOT in main.ts, deliberately: the viewer-role
 * derivation, the decision-9 removal predicate, the §5.2 remove-seat
 * confirm chaining, the re-key visibility rule, and the command
 * compositions (createToken at map centre, applyEffect from the picker
 * spec). main.ts keeps only canvas glue and flow wiring.
 *
 * Pure DOM + callbacks; state comes in per render, commands go out through
 * `actions.send`. Idempotent like every panel.
 */

import type { Command, RoomState, Seat } from "@asarimitsuha/table-core";
import { renderConnectionBanner } from "./connectionBanner.js";
import { renderEffectPicker } from "./effectPicker.js";
import { renderGmRekey } from "./gmRekey.js";
import { renderLogPanel } from "./logPanel.js";
import { renderMapPanel } from "./mapPanel.js";
import { renderRemoveSeatConfirm } from "./removeSeat.js";
import { renderConnectionStatus, renderSeatRoster } from "./seatRoster.js";
import { renderAddTokenForm, renderDeleteTokenList } from "./tokenControls.js";
import { renderTokenInspector } from "./tokenInspector.js";
import { renderTokenMirror } from "./tokenMirror.js";
import { renderTurnClock } from "./turnClock.js";

export interface TablePanelMounts {
  seat: HTMLElement;
  rekey: HTMLElement;
  turnClock: HTMLElement;
  inspector: HTMLElement;
  effectPicker: HTMLElement;
  tokenControls: HTMLElement;
  log: HTMLElement;
  mirror: HTMLElement;
  mapPanel: HTMLElement;
  modal: HTMLElement;
  /** The header status pill's parent. */
  header: HTMLElement;
  /** The full-width disconnect banner's mount. */
  banner: HTMLElement;
}

/** Create and append the sidebar mounts in canonical order. */
export function createTablePanelMounts(
  panelRoot: HTMLElement,
  fixed: { modal: HTMLElement; header: HTMLElement; banner: HTMLElement },
): TablePanelMounts {
  const make = (): HTMLElement => document.createElement("div");
  const mounts: TablePanelMounts = {
    seat: make(),
    rekey: make(),
    turnClock: make(),
    inspector: make(),
    effectPicker: make(),
    tokenControls: make(),
    log: make(),
    mirror: make(),
    mapPanel: make(),
    ...fixed,
  };
  mounts.mirror.className = "visually-hidden-mirror";
  panelRoot.append(
    mounts.seat,
    mounts.rekey,
    mounts.turnClock,
    mounts.inspector,
    mounts.effectPicker,
    mounts.tokenControls,
    mounts.log,
    mounts.mapPanel,
    mounts.mirror,
  );
  return mounts;
}

export interface TablePanelsView {
  state: RoomState;
  ownSeatId: string | null;
  selectedTokenId: string | null;
  /** Inline error for the re-key form (decision 14 retry loop). */
  rekeyError: string | null;
  connectionStatus: string;
  /** Another surface owns the disconnect story (removed seat, re-key). */
  bannerSuppressed: boolean;
  /** The map panel renders once per mount (file inputs must keep state). */
  drawMapPanel: boolean;
}

export interface TablePanelsActions {
  send(command: Command): void;
  onSelectToken(tokenId: string | null): void;
  onSubmitGmKey(key: string): void;
  onUploadMapImage(file: File): void;
}

/** Derived facts a render needs; exported for the caller's own use. */
export function viewerSeat(state: RoomState, ownSeatId: string | null): Seat | undefined {
  return ownSeatId !== null ? state.seats[ownSeatId] : undefined;
}

/**
 * Render every sidebar panel for `view`. Returns whether the map panel was
 * drawn this pass so the caller can maintain its draw-once flag.
 */
export function renderTablePanels(
  mounts: TablePanelMounts,
  view: TablePanelsView,
  actions: TablePanelsActions,
): { mapPanelDrawn: boolean } {
  const { state, ownSeatId, selectedTokenId } = view;
  const ownSeat = viewerSeat(state, ownSeatId);
  const isGm = ownSeat?.role === "gm";
  const send = actions.send;

  renderSeatRoster(
    mounts.seat,
    state,
    { ownSeatId, viewerIsGm: isGm === true },
    {
      onRequestRemoveSeat: (seat) => {
        // §5.2 layer 2: the client ALWAYS confirms the irreversible command.
        renderRemoveSeatConfirm(mounts.modal, seat, {
          onConfirm: (seatId) => send({ type: "removeSeat", seatId }),
        });
      },
    },
  );

  // Decision 14: only a player-seated viewer needs the way back to GM.
  if (ownSeat && !isGm) {
    renderGmRekey(
      mounts.rekey,
      { onSubmitGmKey: actions.onSubmitGmKey },
      { error: view.rekeyError },
    );
  } else {
    mounts.rekey.replaceChildren();
  }

  const selected =
    selectedTokenId !== null ? (state.tokens[selectedTokenId] ?? null) : null;
  renderTokenInspector(
    mounts.inspector,
    selected,
    {
      canEdit:
        selected !== null &&
        ownSeat !== undefined &&
        (isGm === true || selected.ownerSeatId === ownSeat.id),
      effects: selected
        ? Object.values(state.effects).filter((e) => e.targetTokenId === selected.id)
        : [],
      tokens: state.tokens,
      // Decision 9's three parties: GM, applier (via source token), target's
      // owner. Shown-when-true only; the server validates regardless.
      canRemoveEffect: (effect) => {
        const owns = (tokenId: string | null): boolean =>
          tokenId !== null &&
          ownSeat !== undefined &&
          state.tokens[tokenId]?.ownerSeatId === ownSeat.id;
        return isGm === true || owns(effect.sourceTokenId) || owns(effect.targetTokenId);
      },
    },
    {
      onAdjustStat: (tokenId, stat, delta) =>
        send({ type: "adjustStat", tokenId, stat, delta }),
      onSelectToken: actions.onSelectToken,
      onRemoveEffect: (effectId) => send({ type: "removeEffect", effectId }),
    },
  );

  renderTokenMirror(mounts.mirror, state);
  renderTurnClock(
    mounts.turnClock,
    state.timeline,
    state.tokens,
    { isGm: isGm === true },
    {
      onBegin: () => send({ type: "beginTimeline" }),
      onAdvance: () => send({ type: "advanceTurn" }),
      onAddToken: (tokenId) => send({ type: "addToTimeline", tokenId }),
    },
  );
  renderLogPanel(mounts.log, state.log);
  renderEffectPicker(
    mounts.effectPicker,
    { tokens: state.tokens, ownSeatId },
    {
      onApply: (spec) =>
        send({
          type: "applyEffect",
          name: spec.name,
          kind: spec.kind,
          targetTokenId: spec.targetTokenId,
          sourceTokenId: spec.sourceTokenId,
          expiry: spec.expiry,
        }),
    },
  );

  let mapPanelDrawn = false;
  if (isGm) {
    mounts.tokenControls.replaceChildren();
    renderAddTokenForm(mounts.tokenControls, state, {
      onAddToken: (name, ownerSeatId) => {
        send({
          type: "createToken",
          name,
          col: Math.floor(state.map.widthCells / 2),
          row: Math.floor(state.map.heightCells / 2),
          ownerSeatId,
        });
      },
    });
    renderDeleteTokenList(mounts.tokenControls, state, {
      onDeleteToken: (tokenId) => send({ type: "deleteToken", tokenId }),
    });
    if (view.drawMapPanel) {
      mapPanelDrawn = true;
      renderMapPanel(mounts.mapPanel, state.map, {
        onApply: (settings) => send({ type: "setMap", ...settings }),
        onUploadImage: actions.onUploadMapImage,
        onClearImage: () => send({ type: "setMap", imageUrl: null }),
      });
    }
  }

  renderConnectionStatus(mounts.header, view.connectionStatus);
  renderConnectionBanner(mounts.banner, view.connectionStatus, {
    suppressed: view.bannerSuppressed,
  });
  return { mapPanelDrawn };
}
